import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Product } from '../types';

interface BlogPost {
  id: string;
  image: string;
  category: string;
  categoryLink: string;
  title: string;
  excerpt: string;
  link: string;
}

interface HomepageCache {
  seenotyLabelProducts: Product[];
  newArrivals: Product[];
  featuredProducts: Product[];
  blogPosts: BlogPost[];
  timestamp: number;
}

interface HomepageCacheContextType {
  cache: HomepageCache | null;
  setCache: (data: Omit<HomepageCache, 'timestamp'>) => void;
  clearCache: () => void;
  isCacheValid: () => boolean;
}

const HomepageCacheContext = createContext<HomepageCacheContextType | undefined>(undefined);

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const CACHE_KEY = 'homepage_cache';

export const HomepageCacheProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cache, setCacheState] = useState<HomepageCache | null>(() => {
    // Try to load from sessionStorage on initialization
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsedCache = JSON.parse(cached) as HomepageCache;
        // Check if cache is still valid AND has data
        const now = Date.now();
        const isNotExpired = (now - parsedCache.timestamp) < CACHE_DURATION;
        const hasData = parsedCache.seenotyLabelProducts.length > 0 || parsedCache.newArrivals.length > 0;
        if (isNotExpired && hasData) {
          return parsedCache;
        } else {
          // Cache expired or has no data, remove it
          sessionStorage.removeItem(CACHE_KEY);
        }
      }
    } catch (error) {
      console.error('Failed to load homepage cache:', error);
    }
    return null;
  });

  const setCache = useCallback((data: Omit<HomepageCache, 'timestamp'>) => {
    const newCache: HomepageCache = {
      ...data,
      timestamp: Date.now()
    };
    setCacheState(newCache);
    // Also save to sessionStorage
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(newCache));
    } catch (error) {
      console.error('Failed to save homepage cache:', error);
    }
  }, []);

  const clearCache = useCallback(() => {
    setCacheState(null);
    try {
      sessionStorage.removeItem(CACHE_KEY);
    } catch (error) {
      console.error('Failed to clear homepage cache:', error);
    }
  }, []);

  const isCacheValid = useCallback(() => {
    if (!cache) return false;
    const now = Date.now();
    const isNotExpired = (now - cache.timestamp) < CACHE_DURATION;
    // Also check if cache has actual data (not empty arrays)
    const hasData = cache.seenotyLabelProducts.length > 0 || cache.newArrivals.length > 0;
    return isNotExpired && hasData;
  }, [cache]);

  return (
    <HomepageCacheContext.Provider value={{ cache, setCache, clearCache, isCacheValid }}>
      {children}
    </HomepageCacheContext.Provider>
  );
};

export const useHomepageCache = () => {
  const context = useContext(HomepageCacheContext);
  if (context === undefined) {
    throw new Error('useHomepageCache must be used within a HomepageCacheProvider');
  }
  return context;
};

